import { useMutation } from '@tanstack/react-query';
import type { BaseSyntheticEvent } from 'react';
import { useCallback, useContext, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import type { FormState, SubmitHandler, UseFormRegister } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { postAccountIntegrateSendVerificationEmail } from '../../../api/apiClient';
import { allowDomains, disallowDomains } from '../../../utils/env';
import { SnackbarContext } from '../../../utils/snackbar/snackbarProvider';
import { getCurrentTimestampMs } from '../../../utils/time';
import { createEmailInputResolver, type EmailInputType } from '../../emailChange/utils/emailInputSchema';
import { IntegrationConflictCode, extractConflictCode } from '../error-codes';

export type InputsType = EmailInputType;

export type UseIntegrationFormProps = {
  isSuccess: boolean;
  /** 既にサブメールアドレスが登録済みで、送信自体が拒否された場合に true */
  isAlreadyIntegrated: boolean;
  register: UseFormRegister<InputsType>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  formState: FormState<InputsType>;
  email: string;
};

export type UseIntegrationFormOptions = {
  // 入力メールが既存アカウントの場合に、そのメールを login_hint として既存アカウントのログインへ誘導する
  onExistingAccount: (email: string) => void;
  // 統合完了後にログイン元アプリへ戻すため、Actionが付与した client_id を callbackClientId として送信メールAPIへ引き回す
  callbackClientId?: string;
};

export function useIntegrationForm({
  onExistingAccount,
  callbackClientId,
}: UseIntegrationFormOptions): UseIntegrationFormProps {
  const { showBoundary } = useErrorBoundary();
  const snackbar = useContext(SnackbarContext);
  const { mutateAsync } = useMutation({
    mutationFn: (email: string) => postAccountIntegrateSendVerificationEmail({ email, callbackClientId }),
  });
  const { register, handleSubmit, formState } = useForm<InputsType>({
    resolver: createEmailInputResolver({ allowDomains, disallowDomains }),
    mode: 'onBlur',
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [isAlreadyIntegrated, setIsAlreadyIntegrated] = useState(false);
  const [email, setEmail] = useState('');
  const onSubmit: SubmitHandler<InputsType, Promise<void>> = useCallback(
    async (data) => {
      const result = await mutateAsync(data.email);
      // 既にアカウントが存在するメールアドレスの場合は確認メールが送信されないため、既存アカウントのログインへ誘導する
      if (result.response.status === 200) {
        onExistingAccount(data.email);
        return;
      }
      if (result.response.ok) {
        setIsSuccess(true);
        setEmail(data.email);
        return;
      }
      if (result.response.status === 409) {
        // 統合済みの場合はメールアドレスを変えても解決しないため、フォームではなく登録済みの案内へ切り替える
        if (extractConflictCode(result.error) === IntegrationConflictCode.AccountAlreadyIntegrated) {
          setIsAlreadyIntegrated(true);
          return;
        }
        snackbar.add({
          id: getCurrentTimestampMs(),
          message: '入力されたメールアドレスは使用できません。別のメールアドレスを入力してください。',
          notificationLevel: 'error',
          hideCloseButon: false,
        });
        return;
      }
      showBoundary(result.error);
    },
    [mutateAsync, onExistingAccount, showBoundary, snackbar]
  );
  return {
    register,
    isSuccess,
    isAlreadyIntegrated,
    email,
    onSubmit: handleSubmit(onSubmit),
    formState,
  };
}
